import L from 'leaflet';
import { calculateGoogleRoute } from 'v3/utils/googleRoute';

const SEGMENT_COORDINATE_PRECISION = 6;

const formatCoordinate = value => Number(value).toFixed(SEGMENT_COORDINATE_PRECISION);

const buildRouteCacheKey = (validWaypoints = []) =>
	validWaypoints
		.map(({ latLng }) =>
			`${formatCoordinate(latLng.lat)}:${formatCoordinate(latLng.lng)}`
		)
		.join('|');

const buildRouteResponse = ({
	coordinates,
	waypoints,
	totalDistance = 0,
	totalTime = 0,
}) => ({
	name: '',
	summary: {
		totalDistance,
		totalTime,
	},
	coordinates,
	inputWaypoints: waypoints,
	waypoints,
	instructions: [],
});

const callGoogleRoute = async ({ validWaypoints, requestState, routeCache }) => {
	const routeCacheKey = buildRouteCacheKey(validWaypoints);
	const result = await calculateGoogleRoute({
		points: validWaypoints.map(({ latLng }) => ({
			lat: latLng.lat,
			lng: latLng.lng,
		})),
		cache: routeCache,
		cacheKey: routeCacheKey,
		isCurrentRequest: () => !requestState.aborted,
		waypointStopover: false,
		optimizeWaypoints: false,
	});

	if (!result) {
		return null;
	}

	return {
		coordinates: result.coordinates.map(([lat, lng]) =>
		L.latLng(lat, lng)
		),
		totalDistance: result.totalDistance,
		totalTime: result.totalTime,
	};
};

export default function createCooperplaceRouter() {
	const activeRequests = new Set();
	const routeCache = new Map();

	const finalizeRequest = requestState => {
		activeRequests.delete(requestState);
	};

	return {
		abortAll() {
			activeRequests.forEach(requestState => {
				if (requestState && typeof requestState.abort === 'function') {
					requestState.abort();
				}
			});
			activeRequests.clear();
		},
		route(waypoints, callback, context) {
			const ctx = context || callback;
			const validWaypoints = (waypoints || []).filter(waypoint => waypoint && waypoint.latLng);

			const requestState = {
				aborted: false,
				abort() {
					this.aborted = true;
				},
			};

			activeRequests.add(requestState);

			if (validWaypoints.length < 2) {
				finalizeRequest(requestState);
				callback.call(ctx, { status: -1, message: 'Pelo menos dois pontos são necessários.' });
				return requestState;
			}

				Promise.resolve()
					.then(async () => {
						const resolvedRoute = await callGoogleRoute({
							validWaypoints,
							requestState,
							routeCache,
						});

						if (requestState.aborted || !resolvedRoute) return;

						callback.call(ctx, null, [
							buildRouteResponse({
								coordinates: resolvedRoute.coordinates,
								waypoints: validWaypoints,
								totalDistance: resolvedRoute.totalDistance,
								totalTime: resolvedRoute.totalTime,
							}),
						]);
					})
					.catch((error) => {
						if (requestState.aborted) return;
						callback.call(ctx, {
							status: -1,
							message: error?.message || 'Não foi possível calcular a rota com o Google.',
						});
					})
					.finally(() => {
						finalizeRequest(requestState);
				});

			return requestState;
		},
	};
}
